import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConfigurationService } from './config.service';
import {
	CheckEdeFtpCredentialsRequest,
	CheckEdeFtpCredentialsResponse,
	CreateExploitationRequest,
	CreateUserExploitationRequest,
	CreateUserRequest,
	GetAnimalResponse,
	GetEdeFtpCredentialsResponse,
	GetExploitationResponse,
	GetExploitationsResponse,
	GetExploitationStatusCount,
	GetSupportedVersionsResponse,
	GetUserResponse,
	GetUsersResponse,
	GetUsersResponseLite,
	ListMessagesResponse,
	MessageResponse,
	PostEdeFtpCredentialsRequest,
	PostMessageEditRequest,
	PostMessageEditResponse,
	UpdateExploitationRequest,
	UpdateUserRequest,
	UpdateVersionRequest,
} from './dtos';

@Injectable()
export class ApiService {
	private readonly apiBase: string;

	constructor(private readonly http: HttpClient, private readonly configurationService: ConfigurationService) {
		this.apiBase = this.configurationService.configuration.apiBaseUrl;
	}

	private static defaultOptions(): { headers: HttpHeaders } {
		const headers = new HttpHeaders().set('x-api-version', '1.0');
		return { headers };
	}

	private static exploitationOptions(): { headers: HttpHeaders } {
		const headers = new HttpHeaders().set('x-api-version', '1.1');
		return { headers };
	}

	private static userOptions(): { headers: HttpHeaders } {
		const headers = new HttpHeaders().set('x-api-version', '1.2');
		return { headers };
	}

	getVersions(): Observable<GetSupportedVersionsResponse> {
		return this.http.get<GetSupportedVersionsResponse>(
			this.apiBase + '/admin/version/',
			ApiService.defaultOptions(),
		);
	}

	putVersions(body: UpdateVersionRequest): Observable<void> {
		return this.http.put<void>(this.apiBase + '/admin/version/', body, ApiService.defaultOptions());
	}

	exploitation_getAdminExploitations(): Observable<GetExploitationsResponse> {
		return this.http.get<GetExploitationsResponse>(
			this.apiBase + '/admin/exploitation/',
			ApiService.exploitationOptions(),
		);
	}

	exploitation_getAdminExploitation(exploitationId: string): Observable<GetExploitationResponse> {
		return this.http.get<GetExploitationResponse>(
			this.apiBase + '/admin/exploitation/' + exploitationId,
			ApiService.exploitationOptions(),
		);
	}

	exploitation_putAdminExploitation(exploitationId: string, permission: UpdateExploitationRequest): Observable<void> {
		const body = permission;
		return this.http.put<void>(
			this.apiBase + '/admin/exploitation/' + exploitationId,
			body,
			ApiService.exploitationOptions(),
		);
	}

	exploitation_getUsers(): Observable<GetUsersResponseLite> {
		const response = this.http.get<GetUsersResponseLite>(
			this.apiBase + '/admin/user/',
			ApiService.defaultOptions(),
		);
		return response;
	}

	exploitation_getUsersByCheptel(numeroCheptel: string): Observable<GetUsersResponse> {
		const response = this.http.get<GetUsersResponse>(
			this.apiBase + `/admin/user/by-cheptel/${numeroCheptel}`,
			ApiService.defaultOptions(),
		);
		return response;
	}

	exploitation_getUser(userID: string): Observable<GetUserResponse> {
		const response = this.http.get<GetUserResponse>(
			this.apiBase + '/admin/user/' + userID,
			ApiService.defaultOptions(),
		);
		return response;
	}

	exploitation_putUserAccess(userExploitationId: string, niveauAcces: string): Observable<void> {
		const body = { niveauAcces: niveauAcces };
		return this.http.put<void>(
			this.apiBase + '/admin/user-exploitation/' + userExploitationId,
			body,
			ApiService.defaultOptions(),
		);
	}

	exploitation_postExploitation(newCheptel: CreateExploitationRequest): Observable<void> {
		const body = newCheptel;
		return this.http.post<void>(this.apiBase + '/admin/exploitation/', body, ApiService.exploitationOptions());
	}

	user_postUser(newUser: CreateUserRequest): Observable<void> {
		const body = newUser;
		return this.http.post<void>(this.apiBase + '/admin/user/', body, ApiService.defaultOptions());
	}

	user_putUser(updatedUser: UpdateUserRequest, userId: string): Observable<void> {
		const body = updatedUser;
		return this.http.put<void>(this.apiBase + '/admin/user/' + userId, body, ApiService.defaultOptions());
	}

	exploitation_putDown(userExploitationId: string): Observable<void> {
		return this.http.put<void>(
			this.apiBase + '/admin/exploitation/' + userExploitationId + '/disable',
			'nothing',
			ApiService.exploitationOptions(),
		);
	}

	exploitation_putUp(userExploitationId: string): Observable<void> {
		return this.http.put<void>(
			this.apiBase + '/admin/exploitation/' + userExploitationId + '/enable',
			'nothing',
			ApiService.exploitationOptions(),
		);
	}

	exploitation_putSyncDown(userExploitationId: string): Observable<void> {
		return this.http.put<void>(
			this.apiBase + '/admin/exploitation/' + userExploitationId + '/disable-synchronization',
			'nothing',
			ApiService.exploitationOptions(),
		);
	}

	exploitation_putSyncUp(userExploitationId: string): Observable<void> {
		return this.http.put<void>(
			this.apiBase + '/admin/exploitation/' + userExploitationId + '/enable-synchronization',
			'nothing',
			ApiService.exploitationOptions(),
		);
	}

	exploitation_postUserAccess(newAccess: CreateUserExploitationRequest): Observable<void> {
		const body = newAccess;
		return this.http.post<void>(this.apiBase + '/admin/user-exploitation/', body, ApiService.defaultOptions());
	}

	exploitation_deleteUserAccess(userExploitationId: string): Observable<void> {
		return this.http.delete<void>(
			this.apiBase + '/admin/user-exploitation/' + userExploitationId,
			ApiService.defaultOptions(),
		);
	}

	exploitation_getCredentialsEdeFtp(exploitationId: string): Observable<GetEdeFtpCredentialsResponse> {
		const response = this.http.get<GetEdeFtpCredentialsResponse>(
			this.apiBase + '/admin/exploitation/' + exploitationId + '/ftp/',
			ApiService.exploitationOptions(),
		);
		return response;
	}

	exploitation_postCredentialsEdeFtp(
		exploitationId: string,
		credentials: PostEdeFtpCredentialsRequest,
	): Observable<void> {
		const body = credentials;
		const response = this.http.post<void>(
			this.apiBase + '/admin/exploitation/' + exploitationId + '/ftp/',
			body,
			ApiService.exploitationOptions(),
		);
		return response;
	}

	exploitation_checkCredentialsEdeFtp(
		exploitationId: string,
		credentials: CheckEdeFtpCredentialsRequest,
	): Observable<CheckEdeFtpCredentialsResponse> {
		const body = credentials;
		const response = this.http.post<CheckEdeFtpCredentialsResponse>(
			this.apiBase + '/admin/exploitation/' + exploitationId + '/ftp/check',
			body,
			ApiService.exploitationOptions(),
		);
		return response;
	}
	user_deleteUser(userId: string): Observable<void> {
		return this.http.delete<void>(this.apiBase + '/admin/user/' + userId, ApiService.defaultOptions());
	}

	syncExploitation(numeroCheptel: string): Observable<void> {
		const url = `${this.apiBase}/admin/sync/exploitations/init?numeroCheptel=${numeroCheptel}`;
		return this.http.post<void>(url, null, ApiService.defaultOptions());
	}

	syncAnimaux(numeroCheptel: string): Observable<void> {
		const url = `${this.apiBase}/admin/sync/animaux/init?numeroCheptel=${numeroCheptel}`;
		return this.http.post<void>(url, null, ApiService.defaultOptions());
	}

	exploitation_getStatusCounts(): Observable<GetExploitationStatusCount> {
		return this.http.get<GetExploitationStatusCount>(
			this.apiBase + '/admin/chart/exploitations-status',
			ApiService.exploitationOptions(),
		);
	}

	messages_list(): Observable<ListMessagesResponse> {
		return this.http.get<ListMessagesResponse>(this.apiBase + '/admin/message', ApiService.defaultOptions());
	}

	messages_getOne(messageID: string): Observable<MessageResponse> {
		return this.http.get<MessageResponse>(
			this.apiBase + '/admin/message/' + messageID,
			ApiService.defaultOptions(),
		);
	}

	messages_new(request: PostMessageEditRequest): Observable<PostMessageEditResponse> {
		return this.http.post<PostMessageEditResponse>(
			this.apiBase + '/admin/message',
			request,
			ApiService.defaultOptions(),
		);
	}

	messages_update(messageID: string, request: PostMessageEditRequest): Observable<PostMessageEditResponse> {
		return this.http.post<PostMessageEditResponse>(
			this.apiBase + '/admin/message/' + messageID,
			request,
			ApiService.defaultOptions(),
		);
	}

	messages_delete(messageID: string): Observable<void> {
		return this.http.delete<void>(this.apiBase + '/admin/message/' + messageID, ApiService.defaultOptions());
	}

	get_animal_info(
		exploitationID: string,
		numeroCheptel: string,
		numeroNational: string,
		codePays: string,
	): Observable<GetAnimalResponse> {
		return this.http.get<GetAnimalResponse>(
			this.apiBase +
				`/admin/exploitation/${exploitationID}/animal?numeroCheptel=${numeroCheptel}&numeroNational=${numeroNational}&codePays=${codePays}`,
			ApiService.defaultOptions(),
		);
	}

	delete_animal_info(
		exploitationID: string,
		numeroCheptel: string,
		numeroNational: string,
		codePays: string,
	): Observable<void> {
		return this.http.delete<void>(
			this.apiBase +
				`/admin/exploitation/${exploitationID}/animal?numeroCheptel=${numeroCheptel}&numeroNational=${numeroNational}&codePays=${codePays}`,
			ApiService.defaultOptions(),
		);
	}
}
